import React, { useState } from "react";
import {
  Megaphone,
  Check,
  ArrowRight,
  Target,
  Globe2,
  Users,
  Mail,
  Sparkles,
  Image as ImageIcon,
  Video,
  Star,
  LayoutGrid,
} from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { submitSponsor } from "@/lib/api";
import LiveAuctionStrip from "@/components/LiveAuctionStrip";
import AdvertStudioPublic from "@/components/AdvertStudioPublic";

const ICONS = {
  square: LayoutGrid,
  featured: Star,
  banner: ImageIcon,
  video: Video,
};

const SponsorPage = () => {
  const { t, lang } = useLang();
  const allPackages = [
    {
      name: t.sponsor.campaignTitle,
      price: t.sponsor.campaignPrice,
    },
    ...t.sponsor.packages.map((p) => ({ name: p.name, price: p.price })),
  ];

  const [form, setForm] = useState({
    company: "",
    name: "",
    email: "",
    website: "",
    package: allPackages[0].name,
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | sending | ok | error

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company || !form.name || !form.email) return;
    setStatus("sending");
    try {
      await submitSponsor({ ...form, language: lang });
      setStatus("ok");
      setForm((f) => ({ ...f, message: "" }));
    } catch {
      setStatus("error");
    }
  };

  const pickPackage = (name) => {
    update("package", name);
    const el = document.getElementById("sponsor-form");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main>
      {/* HERO */}
      <section className="bg-[#0F172A] text-white border-b border-[#0F2A1F]">
        <div className="hac-container py-16 md:py-24">
          <div className="inline-flex items-center gap-2 bg-[#2563EB] text-white px-3 py-1 rounded-full text-xs font-semibold mb-6">
            <Megaphone size={14} /> {t.sponsor.nav}
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.1] max-w-4xl mb-6">
            {t.sponsor.pageTitle}
          </h1>
          <p className="text-white/80 text-lg leading-relaxed max-w-3xl mb-12">
            {t.sponsor.pageLede}
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl">
            {t.sponsor.stats.map((s) => (
              <div
                key={s.label}
                className="border-l-2 border-[#60A5FA] pl-4"
                data-testid={`sponsor-stat-${s.label}`}
              >
                <div className="text-3xl md:text-4xl font-bold leading-none">{s.value}</div>
                <div className="text-xs md:text-sm text-white/70 mt-2 uppercase tracking-wider">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WEEKLY AUCTION — cheapest entry point, drives urgency above the €499 targeted campaign */}
      <section className="hac-container pt-10 -mb-6" data-testid="sponsor-auction-section">
        <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3 px-1">
          <div>
            <div className="overline text-amber-600">{lang === "nl" ? "Goedkoopste instap deze week" : "Cheapest entry this week"}</div>
            <h2 className="text-xl md:text-2xl font-bold leading-tight mt-0.5">
              {lang === "nl" ? "Wekelijkse veiling · vanaf €25" : "Weekly auction · from €25"}
            </h2>
          </div>
          <p className="text-xs text-[#64748B] max-w-md text-right">
            {lang === "nl"
              ? "1 winnende app per week wordt prominent uitgelicht. Bieden vanaf €25, lijstprijs €499."
              : "1 winning app per week is featured prominently. Bid from €25, list price €499."}
          </p>
        </div>
        <LiveAuctionStrip showFallback={true} />
      </section>

      <AdvertStudioPublic isNl={lang === "nl"} />

      {/* TARGETED CAMPAIGN HERO */}
      <section className="hac-container py-16 md:py-24">
        <div className="grid lg:grid-cols-[1.3fr_1fr] gap-10 items-stretch">
          <div className="bg-white border-2 border-[#2563EB] rounded-2xl p-8 md:p-12 relative overflow-hidden">
            <div className="absolute top-4 right-4 inline-flex items-center gap-1 bg-[#06B6D4] text-white text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full">
              <Sparkles size={12} /> Premium
            </div>
            <div className="flex items-center gap-2 text-[#2563EB] text-xs font-semibold uppercase tracking-widest mb-4">
              <Target size={14} /> Lead generation
            </div>
            <h2 className="text-3xl md:text-4xl font-bold leading-tight mb-3">
              {t.sponsor.campaignTitle}
            </h2>
            <div className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-5">
              {t.sponsor.campaignPrice}
            </div>
            <p className="text-[#64748B] leading-relaxed mb-7 max-w-2xl">
              {t.sponsor.campaignLede}
            </p>
            <ul className="space-y-2.5 mb-8">
              {t.sponsor.campaignPerks.map((perk) => (
                <li key={perk} className="flex items-start gap-3 text-[#0F172A]">
                  <Check size={18} className="text-[#0F172A] mt-0.5 shrink-0" />
                  <span>{perk}</span>
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={() => pickPackage(t.sponsor.campaignTitle)}
              className="hac-btn-primary"
              data-testid="sponsor-campaign-cta"
            >
              {t.sponsor.campaignCta} <ArrowRight size={14} />
            </button>
          </div>

          <div className="grid gap-4">
            <div className="bg-[#F1F5F9] border border-[#E2E8F0] rounded-xl p-6">
              <Users size={20} className="text-[#2563EB] mb-3" />
              <h3 className="font-bold text-lg mb-3">
                {lang === "nl" ? "Branches" : "Sectors"}
              </h3>
              <div className="flex flex-wrap gap-1.5">
                {t.sponsor.sectors.map((s) => (
                  <span key={s} className="hac-chip">
                    {s}
                  </span>
                ))}
              </div>
            </div>
            <div className="bg-[#F1F5F9] border border-[#E2E8F0] rounded-xl p-6">
              <Globe2 size={20} className="text-[#2563EB] mb-3" />
              <h3 className="font-bold text-lg mb-3">
                {lang === "nl" ? "Landen" : "Countries"}
              </h3>
              <div className="flex flex-wrap gap-1.5">
                {t.sponsor.countries.map((c) => (
                  <span key={c} className="hac-chip">
                    {c}
                  </span>
                ))}
              </div>
            </div>
            <div className="bg-[#0F172A] text-white rounded-xl p-6">
              <Mail size={20} className="text-[#60A5FA] mb-3" />
              <p className="text-sm leading-relaxed">{t.sponsor.idealFor}</p>
            </div>
          </div>
        </div>
      </section>

      {/* AD FORMATS */}
      <section className="bg-[#F1F5F9] border-y border-[#E2E8F0] py-16 md:py-24">
        <div className="hac-container">
          <div className="max-w-2xl mb-12">
            <div className="overline mb-3">{t.sponsor.formatsTitle}</div>
            <h2 className="text-3xl md:text-5xl font-bold leading-tight mb-3">
              {t.sponsor.formatsTitle}
            </h2>
            <p className="text-[#64748B] leading-relaxed">{t.sponsor.formatsLede}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {t.sponsor.packages.map((p) => {
              const Icon = ICONS[p.key] || Star;
              return (
                <div
                  key={p.key}
                  className="bg-white border border-[#E2E8F0] rounded-xl p-6 flex flex-col hover:border-[#2563EB] hover:shadow-md transition-all"
                  data-testid={`sponsor-format-${p.key}`}
                >
                  <div className="w-11 h-11 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg flex items-center justify-center text-[#0F172A] mb-4">
                    <Icon size={20} />
                  </div>
                  <h3 className="font-bold text-lg leading-tight mb-1">{p.name}</h3>
                  <div className="text-[#2563EB] font-semibold mb-5">{p.price}</div>
                  <ul className="space-y-2 mb-6 text-sm text-[#64748B] flex-1">
                    {p.perks.map((perk) => (
                      <li key={perk} className="flex items-start gap-2">
                        <Check size={14} className="text-[#0F172A] mt-0.5 shrink-0" />
                        <span>{perk}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    onClick={() => pickPackage(p.name)}
                    className="hac-btn-secondary justify-center w-full"
                    data-testid={`sponsor-pick-${p.key}`}
                  >
                    {t.sponsor.cta} <ArrowRight size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* MEDIA KIT */}
      <section className="hac-container py-8">
        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/media-kit.pdf`} target="_blank" rel="noreferrer" className="block max-w-3xl mx-auto bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-2xl p-6 md:p-7 transition-colors group" data-testid="sponsor-mediakit-download">
          <div className="flex items-center gap-5">
            <div className="text-5xl shrink-0">📄</div>
            <div className="flex-1">
              <div className="text-xs uppercase tracking-widest text-amber-300 font-bold mb-1">{lang === "nl" ? "Voor designers" : "For designers"}</div>
              <div className="font-bold text-lg mb-1">{lang === "nl" ? "Download de Media Kit (PDF)" : "Download the Media Kit (PDF)"}</div>
              <p className="text-sm text-white/70">{lang === "nl" ? "Alle advertentie-afmetingen, formaten, safe-zones en wireframes — 5 pagina's, klaar voor je designteam." : "All ad dimensions, formats, safe zones and wireframes — 5 pages, ready for your design team."}</p>
            </div>
            <span className="text-amber-300 text-2xl group-hover:translate-x-1 transition-transform shrink-0">→</span>
          </div>
        </a>
      </section>

      {/* FORM */}
      <section className="hac-container py-16 md:py-24">
        <div id="sponsor-form" className="max-w-3xl mx-auto bg-white border border-[#E2E8F0] rounded-2xl p-8 md:p-12">
          <h2 className="text-2xl md:text-3xl font-bold mb-2">{t.sponsor.form.title}</h2>
          <p className="text-[#64748B] mb-8">{t.sponsor.idealFor}</p>

          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="overline text-[#64748B] block mb-1.5">
                {t.sponsor.form.company} *
              </label>
              <input
                required
                value={form.company}
                onChange={(e) => update("company", e.target.value)}
                className="hac-input"
                data-testid="sponsor-company"
              />
            </div>
            <div>
              <label className="overline text-[#64748B] block mb-1.5">
                {t.sponsor.form.name} *
              </label>
              <input
                required
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                className="hac-input"
                data-testid="sponsor-name"
              />
            </div>
            <div>
              <label className="overline text-[#64748B] block mb-1.5">
                {t.sponsor.form.email} *
              </label>
              <input
                required
                type="email"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                className="hac-input"
                data-testid="sponsor-email"
              />
            </div>
            <div>
              <label className="overline text-[#64748B] block mb-1.5">
                {t.sponsor.form.website}
              </label>
              <input
                value={form.website}
                onChange={(e) => update("website", e.target.value)}
                className="hac-input"
                placeholder="https://"
                data-testid="sponsor-website"
              />
            </div>
            <div>
              <label className="overline text-[#64748B] block mb-1.5">
                {t.sponsor.form.packageLabel}
              </label>
              <select
                value={form.package}
                onChange={(e) => update("package", e.target.value)}
                className="hac-input"
                data-testid="sponsor-package"
              >
                {allPackages.map((p) => (
                  <option key={p.name} value={p.name}>
                    {p.name} — {p.price}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="overline text-[#64748B] block mb-1.5">
                {t.sponsor.form.message}
              </label>
              <textarea
                rows={4}
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                className="hac-input resize-none"
                data-testid="sponsor-message"
              />
            </div>
            <div className="md:col-span-2 flex items-center justify-between flex-wrap gap-3 pt-2">
              <div className="text-sm">
                {status === "ok" && (
                  <span className="text-[#2563EB] font-medium" data-testid="sponsor-success">
                    ✓ {t.sponsor.form.success}
                  </span>
                )}
                {status === "error" && (
                  <span className="text-[#EF4444]" data-testid="sponsor-error">
                    {t.sponsor.form.error}
                  </span>
                )}
              </div>
              <button
                type="submit"
                disabled={status === "sending"}
                className="hac-btn-primary disabled:opacity-50"
                data-testid="sponsor-submit"
              >
                {t.sponsor.form.submit} <ArrowRight size={14} />
              </button>
            </div>
          </form>
        </div>
      </section>
    </main>
  );
};

export default SponsorPage;
